import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useCallback, useRef, forwardRef } from "react";

// Fisher-Yates shuffle
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Import optimized WebP child images (256x256)
import samImage from "@/assets/children/sam.webp";
import kajsaImage from "@/assets/children/kajsa.webp";
import filipImage from "@/assets/children/filip.webp";
import alfredImage from "@/assets/children/alfred.webp";
import sophieImage from "@/assets/children/sophie.webp";
import jolineImage from "@/assets/children/joline.webp";
import maxImage from "@/assets/children/max.webp";
import willeImage from "@/assets/children/wille.webp";
import angelineImage from "@/assets/children/angeline.webp";
import tindraImage from "@/assets/children/tindra.webp";
import joshImage from "@/assets/children/josh.webp";
import emiliaImage from "@/assets/children/emilia.webp";
import tristanImage from "@/assets/children/tristan.webp";
import valterImage from "@/assets/children/valter.webp";
import lilyImage from "@/assets/children/lily.webp";
import agnesImage from "@/assets/children/agnes.webp";
import laylaImage from "@/assets/children/layla.webp";

interface ChildData {
  name: string;
  age: number;
  image: string;
}

const childrenData: ChildData[] = [
  { name: "Sam", age: 5, image: samImage },
  { name: "Kajsa", age: 6, image: kajsaImage },
  { name: "Filip", age: 10, image: filipImage },
  { name: "Alfred", age: 7, image: alfredImage },
  { name: "Sophie", age: 3, image: sophieImage },
  { name: "Joline", age: 10, image: jolineImage },
  { name: "Max", age: 3, image: maxImage },
  { name: "Wille", age: 3, image: willeImage },
  { name: "Angeline", age: 9, image: angelineImage },
  { name: "Tindra", age: 7, image: tindraImage },
  { name: "Josh", age: 7, image: joshImage },
  { name: "Emilia", age: 6, image: emiliaImage },
  { name: "Tristan", age: 8, image: tristanImage },
  { name: "Valter", age: 6, image: valterImage },
  { name: "Lily", age: 4, image: lilyImage },
  { name: "Agnes", age: 5, image: agnesImage },
  { name: "Layla", age: 2, image: laylaImage },
];

interface FloatingConfig {
  left: number;
  size: number;
  duration: number;
  sway: number;
}

interface ActiveChild {
  id: string;
  child: ChildData;
  config: FloatingConfig;
}

interface FloatingChildProps {
  instanceId: string;
  child: ChildData;
  config: FloatingConfig;
  onDone: (id: string) => void;
}

const FloatingChild = forwardRef<HTMLDivElement, FloatingChildProps>(
  ({ instanceId, child, config, onDone }, ref) => {
    const { left, size, duration, sway } = config;

    return (
      <motion.div
        ref={ref}
        className="absolute bottom-0 pointer-events-none flex flex-col items-center"
        style={{ left: `${left}%` }}
        initial={{ y: 0, opacity: 0, scale: 0.6 }}
        animate={{
          y: [0, -220, -340],
          opacity: [0, 0.95, 0],
          scale: [0.6, 1, 0.85],
          x: [0, sway, -sway / 2],
        }}
        transition={{
          duration: duration,
          ease: "easeOut",
          times: [0, 0.65, 1],
        }}
        onAnimationComplete={() => onDone(instanceId)}
      >
        {/* Child image - 20% larger (96-125px) */}
        <div 
          className="rounded-full overflow-hidden border-4 border-white shadow-lg"
          style={{ width: size, height: size }}
        >
          <img 
            src={child.image} 
            alt={`Illustration av barnet ${child.name}, ${child.age} år`}
            className="w-full h-full object-cover"
            width={128}
            height={128}
            loading="lazy"
            decoding="async"
          />
        </div>
        
        {/* Name and age label - 20% larger */}
        <div className="mt-2 px-4 py-1.5 bg-white/90 rounded-full shadow-sm">
          <span className="text-base font-medium text-foreground whitespace-nowrap">
            {child.name}, {child.age} år
          </span>
        </div>
      </motion.div>
    );
  }
);

FloatingChild.displayName = "FloatingChild";

const MIN_VISIBLE = 2;
const MAX_VISIBLE = 4; // Reduced to prevent crowding
const SPAWN_MIN_MS = 1400;
const SPAWN_MAX_MS = 2800;
const MIN_HORIZONTAL_DISTANCE = 20; // Minimum % distance between children

const generateConfig = (existingPositions: number[]): FloatingConfig | null => {
  // Try to find a non-overlapping position
  const maxAttempts = 10;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const left = 8 + Math.random() * 84; // 8% to 92%
    
    // Check if this position is far enough from all existing positions
    const isFarEnough = existingPositions.every(
      existingLeft => Math.abs(left - existingLeft) >= MIN_HORIZONTAL_DISTANCE
    );
    
    if (isFarEnough || existingPositions.length === 0) {
      return {
        left,
        size: 96 + Math.random() * 29,
        duration: 8.5 + Math.random() * 3,
        sway: 10 + Math.random() * 15,
      };
    }
  }
  
  // If no valid position found after max attempts, skip spawning
  return null;
};

const FloatingChildren = () => {
  const [active, setActive] = useState<ActiveChild[]>([]);
  const idCounterRef = useRef(0);
  const shuffleQueueRef = useRef<ChildData[]>([]);
  const activeRef = useRef<ActiveChild[]>([]);

  // Keep activeRef in sync with state
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  // Get next child from shuffle queue (refills when empty)
  const getNextChild = useCallback((): ChildData => {
    if (shuffleQueueRef.current.length === 0) {
      shuffleQueueRef.current = shuffleArray([...childrenData]);
    }
    return shuffleQueueRef.current.shift()!;
  }, []);

  const spawnChild = useCallback(() => {
    const existingPositions = activeRef.current.map(a => a.config.left);
    const config = generateConfig(existingPositions);
    
    // Skip if no valid position found
    if (!config) return;
    
    const id = `child-${idCounterRef.current++}`;
    const child = getNextChild();
    setActive(prev => [...prev, { id, child, config }]);
  }, [getNextChild]);

  const handleDone = useCallback((id: string) => {
    setActive(prev => prev.filter(x => x.id !== id));
  }, []);

  // Store spawnChild in ref to avoid stale closures
  const spawnChildRef = useRef(spawnChild);
  useEffect(() => {
    spawnChildRef.current = spawnChild;
  }, [spawnChild]);

  // Initial spawn: start with 2-3 children immediately
  useEffect(() => {
    const initialCount = 2 + Math.floor(Math.random() * 2); // 2-3
    for (let i = 0; i < initialCount; i++) {
      setTimeout(() => spawnChildRef.current(), i * 300);
    }
  }, []);

  // Continuous spawn loop using refs to avoid stale closures
  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout>;

    const scheduleNextSpawn = () => {
      const delay = SPAWN_MIN_MS + Math.random() * (SPAWN_MAX_MS - SPAWN_MIN_MS);
      timerId = setTimeout(() => {
        const currentCount = activeRef.current.length;
        
        // Spawn if below max, spawn faster if below min
        if (currentCount < MAX_VISIBLE) {
          if (currentCount < MIN_VISIBLE || Math.random() > 0.2) {
            spawnChildRef.current();
          }
        }
        
        // Schedule next spawn
        scheduleNextSpawn();
      }, delay);
    };

    // Start the loop after initial spawn
    const startDelay = setTimeout(() => {
      scheduleNextSpawn();
    }, 1000);

    return () => {
      clearTimeout(startDelay);
      clearTimeout(timerId);
    };
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <AnimatePresence>
        {active.map(({ id, child, config }) => (
          <FloatingChild
            key={id}
            instanceId={id}
            child={child}
            config={config}
            onDone={handleDone}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

export default FloatingChildren;
